
import { Component, Vue } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import { GeocodeResult } from '@googlemaps/google-maps-services-js';

import GoogleMaps from '../../anonymous/GoogleMaps.vue';

import TextField from '../../../../framework/fields/TextField.vue';
import SelectField from '../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../framework/fields/SwitchField.vue';

import { sharedState } from '../../../../framework/state';
import { postResource } from '../../../../framework/client/resource';
import { getEnumerationOptions, getGroupOptions } from '../../../../framework/service/options';

import { CustomerType } from '../../../../../common/application/model/enums/CustomerType';
import { Customer } from '../../../../../common/application/model/Customer';
import { getCustomerOptions } from '../../../service/application_options_service';
import { CustomerState } from '../../../../../common/application/model/enums/CustomerState';
import { InvoiceType } from '../../../../../common/application/model/enums/InvoiceType';
import { PaymentTerms } from '../../../../../common/application/model/enums/PaymentTerms';
import { Iso4217CurrencyCode } from '../../../../../common/application/model/enums/Iso4217CurrencyCode';
import { parseGeocodeResult } from '../../../../../common/application/service/google_maps_service';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import { errorToast, successToast } from '../../../service/toast_service';
import { updateCachedResourceRow } from '../../../service/query_cache_service';
import { InvoicingLanguage } from '../../../../../common/application/model/enums/InvoicingLanguage';

@Component({
    components: { SwitchField, SelectField, TextField, GoogleMaps, ValidationObserver },
})
export default class AddCustomer extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.CUSTOMER;

    // Properties

    // State
    shared = sharedState;
    local = {
        row: {
            aggregationInvoice: true,
            currency: Iso4217CurrencyCode.EUR,
            customerState: CustomerState.ACTIVE,
            euCustomer: false,
            freezeAssets: false,
            invoiceable: false,
            invoiceType: InvoiceType.EINVOICE,
            paymentTerms: PaymentTerms.TERM_30_DAYS,
            salesInvoiceSummaryByVehicle: true,
            invoicingLanguage: InvoicingLanguage.FI,
        } as Customer,
        customerTypeOptions: [] as { id: string | undefined; label: string }[],
        customerStateOptions: [] as { id: string | undefined; label: string }[],
        invoiceTypeOptions: [] as { id: string | undefined; label: string }[],
        paymentTermsOptions: [] as { id: string | undefined; label: string }[],
        groupOptions: [] as { id: string | undefined; label: string }[],
        customerOptions: [] as { id: string | undefined; label: string }[],
        currencyOptions: [] as { id: string | undefined; label: string }[],
        invoicingLanguageOptions: [] as { id: string | undefined; label: string }[],
    };

    // Functions
    async mounted() {
        this.local.customerTypeOptions = getEnumerationOptions('CustomerType', Object.keys(CustomerType), this);
        this.local.customerStateOptions = getEnumerationOptions('CustomerState', Object.keys(CustomerState), this);
        this.local.invoiceTypeOptions = getEnumerationOptions('InvoiceType', Object.keys(InvoiceType), this);
        this.local.paymentTermsOptions = getEnumerationOptions('PaymentTerms', Object.keys(PaymentTerms), this);
        this.local.currencyOptions = getEnumerationOptions(
            'Iso4217CurrencyCode',
            Object.keys(Iso4217CurrencyCode),
            this,
        );
        this.local.invoicingLanguageOptions = getEnumerationOptions(
            'InvoicingLanguage',
            Object.keys(InvoicingLanguage),
            this,
        );
        this.local.groupOptions = await getGroupOptions();
        this.local.customerOptions = await getCustomerOptions();
    }

    add() {
        this.addResource(false);
    }

    addAndClose() {
        this.addResource(true);
    }

    async addResource(goBack: boolean) {
        try {
            const newCustomer = await postResource<Customer>(this.resourceType, this.local.row);
            successToast(this, 'message.customerSaved');
            updateCachedResourceRow<Customer>(newCustomer, newCustomer.id, this.resourceType);

            if (goBack) {
                this.back();
            } else {
                this.$router.replace(`/${this.resourceType}/${newCustomer.id}/edit`);
            }
        } catch (error) {
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }

    get fullAddress(): string {
        if (!this.local.row.invoicingAddressStreet) {
            return '';
        }

        return `${this.local.row.invoicingAddressStreet}, ${this.local.row.invoicingAddressZip} ${this.local.row.invoicingAddressCity}`;
    }

    get hasAddress(): boolean {
        return !!(
            this.local.row.invoicingAddressStreet &&
            this.local.row.invoicingAddressZip &&
            this.local.row.invoicingAddressCity &&
            this.local.row.invoicingAddressCountry
        );
    }

    setAddress(geocodeResult: GeocodeResult) {
        const parsedLocation = parseGeocodeResult(geocodeResult);

        this.local.row = {
            ...this.local.row,
            invoicingAddressStreet: parsedLocation.street,
            invoicingAddressZip: parsedLocation.zip,
            invoicingAddressCity: parsedLocation.city,
            invoicingAddressCountry: parsedLocation.country,
            lat: parsedLocation.lat,
            lng: parsedLocation.lng,
        };
    }
}
